import { motion } from "framer-motion";
import {
  TrendingUp,
  BookText,
  CandlestickChart,
  Waves,
  Crosshair,
  ShieldAlert,
  Brain,
  AlertOctagon,
} from "lucide-react";

const TOPICS = [
  { icon: TrendingUp, title: "Trading Fundamentals", desc: "How markets actually work, from orders to execution." },
  { icon: BookText, title: "Market Terminology", desc: "Bid, ask, spread, slippage — explained in plain English." },
  { icon: CandlestickChart, title: "Candlestick Patterns", desc: "Read price action like a story, candle by candle." },
  { icon: Waves, title: "Liquidity & Volatility", desc: "Why some moves explode and others fade — and how to spot it." },
  { icon: Crosshair, title: "Entry & Exit Strategies", desc: "Frameworks for timing your trades with confidence." },
  { icon: ShieldAlert, title: "Risk Management", desc: "Position sizing, stop losses, and protecting your capital." },
  { icon: Brain, title: "Trading Psychology", desc: "Master fear, FOMO, and the mental game of trading." },
  { icon: AlertOctagon, title: "Common Beginner Mistakes", desc: "The 12 traps that wipe out new traders — and how to avoid them." },
];

export default function Learn() {
  return (
    <section id="learn" data-testid="learn-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-semibold font-display mb-4">
            What you'll learn
          </div>
          <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            Everything you need to <span className="text-emerald-400">start trading</span> — with clarity.
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg max-w-2xl">
            Eight focused chapters, written for absolute beginners. No jargon, no fluff — just the
            foundations that take you from confused to confident.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TOPICS.map((t, i) => (
            <motion.div
              key={t.title}
              data-testid={`learn-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group relative p-6 rounded-2xl bg-[#0c0c0c] border border-white/10 hover:border-emerald-500/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_-10px_rgba(16,185,129,0.4)]"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 grid place-items-center mb-5 group-hover:bg-emerald-500/20 transition">
                <t.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-xs text-white/40 font-display tracking-widest mb-1">
                0{i + 1}
              </div>
              <h3 className="font-display font-semibold text-white text-lg tracking-tight">
                {t.title}
              </h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{t.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
